import {
  View,
  Text,
  StyleSheet,
  Font,
  Page,
  Document,
} from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { RubricGeneratorResponseDto } from "@packages/api/madlenAPI.schemas";
import MarkdownRenderer from "../custom-markdown";
import { CourseData } from "@packages/constant/CourseData";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { format } from "date-fns";
import { tr, enUS } from "date-fns/locale";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  page: {
    padding: 20,
    backgroundColor: "#fff",
    fontFamily: "Montserrat",
  },
  header: {
    flexDirection: "row",
    marginBottom: 20,
    padding: 10,
    backgroundColor: "#f3f4f6",
    borderRadius: 4,
  },
  headerInfo: {
    flex: 1,
  },
  headerText: {
    fontFamily: "Montserrat",
    fontSize: 11,
    lineHeight: 1.4,
    color: "#4A7759",
    marginBottom: 5,
  },
  metaContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 15,
    backgroundColor: "#f3f4f6",
    padding: 8,
    borderRadius: 6,
  },
  metaBadge: {
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
    fontFamily: "Montserrat",
    lineHeight: 1.4,
  },
  scoreBadge: {
    backgroundColor: "#E8F1EB",
    color: "#4A7759",
  },
  levelsBadge: {
    backgroundColor: "#E8F1EB",
    color: "#4A7759",
  },
  table: {
    width: "100%",
    marginTop: 10,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    minHeight: 28,
  },
  tableHeader: {
    backgroundColor: "#f9fafb",
  },
  tableHeaderCell: {
    padding: 8,
    fontSize: 9,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#374151",
    borderRightWidth: 1,
    borderRightColor: "#e5e7eb",
    lineHeight: 1.4,
  },
  tableCell: {
    padding: 8,
    fontSize: 9,
    fontFamily: "Montserrat",
    color: "#374151",
    borderRightWidth: 1,
    borderRightColor: "#e5e7eb",
    lineHeight: 1.4,
  },
  weightCell: {
    width: "8%",
    textAlign: "center",
  },
  criterionCell: {
    width: "22%",
  },
  levelCell: {
    flex: 1,
  },
  levelCellCenter: {
    flex: 1,
    textAlign: "center",
  },
  descriptionCell: {
    color: "#6b7280",
    fontSize: 8,
    lineHeight: 1.4,
  },
  rubricType: {
    fontFamily: "Montserrat",
    fontSize: 11,
    lineHeight: 1.4,
    color: "#4A7759",
    backgroundColor: "#E8F1EB",
    padding: 8,
    borderRadius: 4,
    marginBottom: 15,
  },
  criterionText: {
    fontFamily: "Montserrat",
    fontSize: 9,
    lineHeight: 1.4,
    fontWeight: "bold",
  },
  descriptorText: {
    fontFamily: "Montserrat",
    fontSize: 9,
    lineHeight: 1.4,
  },
  holisticLevelCell: {
    width: "10%",
    textAlign: "center",
  },
  holisticCriterionCell: {
    flex: 1,
  },
  holisticIntervalCell: {
    width: "15%",
    textAlign: "center",
  },
});

interface RubricCardProps {
  rubric: RubricGeneratorResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const RubricDocument = ({ rubric, customizationSettings }: RubricCardProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "tr" ? tr : enUS;
  const headerTextStyle = [
    styles.headerText,
    {
      fontSize: scaleFontSize(
        styles.headerText.fontSize,
        customizationSettings?.fontSize,
      ),
    },
  ];

  return (
    <Document title={rubric.title || rubric.prompt || ""}>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerInfo}>
            <Text style={headerTextStyle}>
              {t(
                `courseType.${CourseData[
                  rubric.courseName as keyof typeof CourseData
                ]?.name(Number(rubric.gradeLevel ?? 8))}`,
              )}{" "}
              -{" "}
              {t("hooks.form.gradeOf", {
                grade: rubric.gradeLevel,
              })}
            </Text>
            <Text style={headerTextStyle}>{rubric.title || rubric.prompt}</Text>
            {rubric.authorName && customizationSettings.showTeacherName && (
              <Text style={headerTextStyle}>{rubric.authorName}</Text>
            )}
            {rubric.createdAt && customizationSettings.showDate && (
              <Text style={headerTextStyle}>
                {format(new Date(rubric.createdAt), "dd MMMM yyyy", {
                  locale,
                })}
              </Text>
            )}
          </View>
        </View>

        {/* Meta Information */}
        <View style={styles.metaContainer}>
          <Text
            style={[
              styles.metaBadge,
              styles.scoreBadge,
              {
                fontSize: scaleFontSize(
                  styles.metaBadge.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {t("rubric.totalScore")}: {rubric.totalScore}
          </Text>
        </View>

        {/* Rubric Type & Description */}
        <Text
          style={[
            styles.rubricType,
            {
              fontSize: scaleFontSize(
                styles.rubricType.fontSize,
                customizationSettings?.fontSize,
              ),
            },
          ]}
        >
          {rubric.description}
        </Text>

        {/* ANALYTIC Rubric Tables */}
        {rubric.rubricType === "ANALYTIC" &&
          rubric.criteria &&
          rubric.criteria.map((criterion, index) => (
            <View key={index} style={styles.table} wrap={false}>
              {/* Table Header */}
              <View style={[styles.tableRow, styles.tableHeader]}>
                <View
                  style={[
                    styles.tableHeaderCell,
                    styles.weightCell,
                    {
                      fontSize: scaleFontSize(
                        styles.tableHeaderCell.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  <Text>{t("rubric.weight")}</Text>
                </View>
                <View
                  style={[
                    styles.tableHeaderCell,
                    styles.criterionCell,
                    {
                      fontSize: scaleFontSize(
                        styles.tableHeaderCell.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  <Text>{t("rubric.criterion.name")}</Text>
                </View>
                {criterion.level_scores?.map((score, idx) => (
                  <View
                    key={idx}
                    style={[
                      styles.tableHeaderCell,
                      styles.levelCellCenter,
                      {
                        fontSize: scaleFontSize(
                          styles.tableHeaderCell.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    <Text>{score}</Text>
                  </View>
                ))}
              </View>

              {/* Criterion Row */}
              <View style={styles.tableRow}>
                <View
                  style={[
                    styles.tableCell,
                    styles.weightCell,
                    {
                      fontSize: scaleFontSize(
                        styles.tableCell.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  <Text>{criterion.weight}%</Text>
                </View>
                <View
                  style={[
                    styles.tableCell,
                    styles.criterionCell,
                    {
                      fontSize: scaleFontSize(
                        styles.tableCell.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          ...styles.criterionText,
                          fontSize: scaleFontSize(
                            styles.criterionText.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                        container: { marginBottom: 0 },
                      },
                    }}
                    content={criterion.criterion || ""}
                  />
                </View>
                {criterion.level_descriptors?.map((descriptor, idx) => (
                  <View
                    key={idx}
                    style={[
                      styles.tableCell,
                      styles.levelCell,
                      {
                        fontSize: scaleFontSize(
                          styles.tableCell.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    <MarkdownRenderer
                      customStyle={{
                        text: {
                          text: {
                            ...styles.descriptorText,
                            fontSize: scaleFontSize(
                              styles.descriptorText.fontSize,
                              customizationSettings?.fontSize,
                            ),
                          },
                          container: { marginBottom: 0 },
                        },
                      }}
                      content={descriptor}
                    />
                  </View>
                ))}
              </View>
            </View>
          ))}

        {rubric.rubricType === "HOLISTIC" && rubric.levelDescriptions && (
          <View style={styles.table} wrap={false}>
            {/* Table Header */}
            <View style={[styles.tableRow, styles.tableHeader]}>
              <View
                style={[
                  styles.tableHeaderCell,
                  styles.holisticLevelCell,
                  {
                    fontSize: scaleFontSize(
                      styles.tableHeaderCell.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                <Text>{t("rubric.level")}</Text>
              </View>
              <View
                style={[
                  styles.tableHeaderCell,
                  styles.holisticCriterionCell,
                  {
                    fontSize: scaleFontSize(
                      styles.tableHeaderCell.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                <Text>{t("rubric.criterion.name")}</Text>
              </View>
              <View
                style={[
                  styles.tableHeaderCell,
                  styles.holisticIntervalCell,
                  {
                    fontSize: scaleFontSize(
                      styles.tableHeaderCell.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                <Text>{t("rubric.interval")}</Text>
              </View>
            </View>

            {/* Level Rows */}
            {rubric.levelDescriptions.map((description, index) => (
              <View key={index} style={styles.tableRow}>
                <View
                  style={[
                    styles.tableCell,
                    styles.holisticLevelCell,
                    {
                      fontSize: scaleFontSize(
                        styles.tableCell.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  <Text>{index + 1}</Text>
                </View>
                <View
                  style={[
                    styles.tableCell,
                    styles.holisticCriterionCell,
                    {
                      fontSize: scaleFontSize(
                        styles.tableCell.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          ...styles.descriptorText,
                          fontSize: scaleFontSize(
                            styles.descriptorText.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                        container: { marginBottom: 0 },
                      },
                    }}
                    content={description}
                  />
                </View>
                <View
                  style={[
                    styles.tableCell,
                    styles.holisticIntervalCell,
                    {
                      fontSize: scaleFontSize(
                        styles.tableCell.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  <Text>
                    {rubric.scoreRanges && rubric.scoreRanges[index]
                      ? `${rubric.scoreRanges[index].min}-${rubric.scoreRanges[index].max}`
                      : "-"}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </Page>
    </Document>
  );
};

function RubricCardPDF(props: RubricCardProps) {
  return <RubricDocument {...props} />;
}

export default RubricCardPDF;
